import { useState } from 'react'

import { GetPlayerWithDepositData } from '@/modules/manage-player/type'
import { columns } from '../PlayerWithDepositTable/constants'
import { DataTableArrow } from '@/common/ui/table/data-table-arrow'
import { Card } from '@/common/ui/card'
import { Separator } from '@/common/ui/separator'
import { DateRangePicker } from '@/common/components/DateRangePicker'
import DateRangeButtons from '@/common/components/DateRangeButtons'
import { Button } from '@/common/ui/button'
import { ArrowLeft, Download } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import PlayerSearchControls from '@/common/components/PlayerSearchControls'
import { Checkbox } from '@/common/ui/checkbox'
import { useQueryState, parseAsBoolean } from 'nuqs'
import { useMutationExportPlayerWithDepositCsv } from '@/modules/manage-player/api/hooks/useMutationExportPlayerWithDepositCsv'
import { ExportCsvAmountModal } from '@/common/components/ExportCsvAmountModal'

import type { ExportPlayerWithDepositCsvRequest } from '@/modules/manage-player/type'

interface PlayerWithDepositListingProps {
	data: GetPlayerWithDepositData[]
	total: number
	isLoading?: boolean
	showBackButton?: boolean
	onSort?: (columnId: string, order: 'asc' | 'desc' | undefined) => void
	exportParams?: Omit<
		ExportPlayerWithDepositCsvRequest,
		'take' | 'skip' | 'filename' | 'columnMappings'
	>
}

export default function PlayerWithDepositListing({
	data,
	total,
	isLoading,
	onSort,
	showBackButton = true,
	exportParams,
}: PlayerWithDepositListingProps) {
	const navigate = useNavigate()
	const exportCsvMutation = useMutationExportPlayerWithDepositCsv()
	const [showExportModal, setShowExportModal] = useState(false)
	const [excludeFreeCredit, setExcludeFreeCredit] = useQueryState(
		'excludeFreeCredit',
		parseAsBoolean.withOptions({ shallow: false }),
	)

	const handleExportCsv = (amount: number) => {
		if (!exportParams) return

		exportCsvMutation.mutate({
			...exportParams,
			take: amount || total || 1000,
			skip: 0,
			filename: 'players_with_deposit',
			columnMappings: {
				phone: 'เบอร์โทรศัพท์',
				totalDepositAmount: 'ยอดฝากทั้งหมด (บาท)',
				totalDepositTime: 'จำนวนครั้ง',
				'lastDeposit.createdAt': 'ฝากล่าสุดเมื่อ',
				lastOnline: 'ใช้งานล่าสุด',
			},
		})
	}

	return (
		<Card>
			<div className="space-y-4 p-3 sm:p-4">
				{showBackButton && (
					<Button
						onClick={() => navigate(-1)}
						variant="outline"
						size="sm"
						className="mb-4 flex items-center gap-2"
					>
						<ArrowLeft className="h-4 w-4" />
						ย้อนกลับ
					</Button>
				)}

				{/* Header Section - Mobile Responsive */}
				<div className="space-y-4">
					{/* Title and Date Controls Row */}
					<div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
						{/* Title */}
						<div>
							<h2 className="text-xl font-bold tracking-tight sm:text-2xl">
								ผู้เล่นที่มียอดฝาก
							</h2>
							<p className="text-sm text-muted-foreground sm:text-base">
								รายการผู้เล่นที่มียอดฝากทั้งหมด {total}
							</p>
						</div>

						{/* Date Controls and Export */}
						<div className="flex flex-col gap-3 sm:items-end">
							<div className="flex flex-col gap-2 sm:flex-row sm:items-center">
								<Button
									variant="outline"
									size="sm"
									onClick={() => setShowExportModal(true)}
									disabled={
										exportCsvMutation.isPending || !exportParams || !data.length
									}
									className="flex items-center gap-2"
								>
									<Download className="h-4 w-4" />
									<span className="sm:inline">โหลด CSV</span>
								</Button>
								<DateRangePicker />
							</div>
							<DateRangeButtons />
						</div>
					</div>

					<div className="flex space-x-4">
						<PlayerSearchControls />

						{/* Exclude Free Credit Checkbox */}
						<div className="flex items-center space-x-2">
							<Checkbox
								id="excludeFreeCredit"
								checked={excludeFreeCredit || false}
								onCheckedChange={(checked) => {
									setExcludeFreeCredit(checked === true ? true : null)
								}}
							/>
							<label
								htmlFor="excludeFreeCredit"
								className="cursor-pointer text-sm"
							>
								ไม่รวมเครดิตฟรี
							</label>
						</div>
					</div>
				</div>

				<Separator className="my-4 sm:my-6" />

				{/* Table Section */}
				<div className="space-y-4">
					<DataTableArrow
						columns={columns}
						data={data}
						total={total}
						isLoading={isLoading}
						onSort={onSort}
					/>
				</div>
			</div>
			<ExportCsvAmountModal
				open={showExportModal}
				onOpenChange={setShowExportModal}
				onConfirm={handleExportCsv}
				defaultAmount={total || 1000}
				maxAmount={100000}
				isLoading={exportCsvMutation.isPending}
			/>
		</Card>
	)
}
